import { skipToken } from '@reduxjs/toolkit/query';
import StatusChip from 'components/@extended/StatusChip.tsx';
import GridItemList, { infoLabel, Row } from 'components/GridItemList';
import { FormattedDate } from 'components/misc/FormattedDate';
import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import MoneyDisplay from 'components/util/MoneyDisplay.tsx';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useGetContactByIdQuery } from 'store/api/contacts';
import { useGetMinOfficesWithFiltersQuery } from 'store/api/offices.ts';
import { useGetRankingsRefereeNominationsQuery } from 'store/api/rankingsReferees';
import { ExperienceRecordDetailsDto } from 'types/experience-records';
import { FeatureFlag } from 'types/features';
import { OfficeMin } from 'types/offices.ts';
import { MinDto } from 'types/other';
import { getClassifierText, t } from 'utils/getTranslations';
import { sortByField } from 'utils/sort';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { getCaseChips, getLinks } from '../../matters/util.tsx';
import { nominationName } from '../../rankings-referees/nominationName';
import { XrConfidentialMarker } from '../components/XrConfidentialMarker';
import { ExperienceRecordSection } from '../form-sections/ExperienceRecordSection';

interface Props {
    experienceRecord: ExperienceRecordDetailsDto;
}

const GeneralView: React.FC<Props> = ({ experienceRecord }) => {
    const isNew = useIsNewDesign();
    const { classifiers } = useClassifiers({
        codes: ['BUSINESS_AREA_TYPE', 'COUNTRY', 'LEGAL_DIRECTORY'],
    });

    // With Rankings referees on, the referees are candidates, and their nominations show under them, read only.
    const hasRankingsReferees = useIsFeatureEnabled(FeatureFlag.RANKINGS_REFEREE);
    const candidates = sortByField(experienceRecord.parties ?? [], 'name');
    const candidateFilter = `referee.id=in=(${candidates.map(({ id }) => id).join(',')})`;
    const { data: candidateNominations } = useGetRankingsRefereeNominationsQuery(
        {
            filters: candidateFilter,
            sort: 'requestedOn,desc',
            page: 0,
            pageSize: 500,
        },
        { skip: !hasRankingsReferees || candidates.length === 0 }
    );
    const nominationsOf = (partyId: number) =>
        (candidateNominations?.content ?? []).filter(({ referee }) => referee.id === partyId);
    const nominatedCandidates = candidates.filter(({ id }) => nominationsOf(id).length > 0);

    const { data: clientDetails } = useGetContactByIdQuery(experienceRecord.clientParty.id.toString() ?? skipToken);
    const connectedBusinessAreas =
        clientDetails?.businessAreas
            ?.map((ba) => getClassifierText(classifiers['BUSINESS_AREA_TYPE'], ba.businessAreaCode))
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .join(', ') || '';
    const connectedSectors = clientDetails?.servedBySectorGroups?.map((s) => s.name).join(', ') || '';

    const { data: officeListData } = useGetMinOfficesWithFiltersQuery({
        page: 0,
        pageSize: 2000,
    });

    // Memoized derived team data for better performance
    const derivedTeamData = useMemo(() => {
        if (!experienceRecord?.matters || experienceRecord.matters.length === 0) {
            return {
                supervisedByEmployees: [],
                responsibleEmployees: [],
                handledByEmployees: [],
                connectedPracticeAreas: [],
                connectedOffices: [],
            };
        }

        // Extract team data from matters - create maps to avoid duplicates by ID
        const supervisedByEmployeesMap = new Map<number, MinDto>();
        experienceRecord.matters.forEach((matter) => {
            if (matter.supervisedByEmployee?.id) {
                supervisedByEmployeesMap.set(matter.supervisedByEmployee.id, matter.supervisedByEmployee);
            }
        });
        const supervisedByEmployees = Array.from(supervisedByEmployeesMap.values());

        const responsibleEmployeesMap = new Map<number, MinDto>();
        experienceRecord.matters.forEach((matter) => {
            if (matter.responsibleEmployee?.id) {
                responsibleEmployeesMap.set(matter.responsibleEmployee.id, matter.responsibleEmployee);
            }
        });
        const responsibleEmployees = Array.from(responsibleEmployeesMap.values());

        const handledByEmployeesMap = new Map<number, MinDto>();
        experienceRecord.matters.forEach((matter) => {
            matter.employees
                ?.filter((emp) => emp.matterEmployeeRoleTypeCode === 'STAFF')
                .forEach((emp) => {
                    if (emp.employee?.id) {
                        handledByEmployeesMap.set(emp.employee.id, emp.employee);
                    }
                });
        });
        const handledByEmployees = Array.from(handledByEmployeesMap.values());

        const practiceAreas = [
            ...new Set(experienceRecord.matters.flatMap((matter) => matter.practiceAreas || []).filter(Boolean)),
        ];

        const distinctOfficesMap = new Map<number, OfficeMin>();
        experienceRecord.matters
            .map((matter) => matter.handledByOffice)
            .forEach((office) => {
                if (office && !distinctOfficesMap.has(office.id)) {
                    distinctOfficesMap.set(office.id, office);
                }
            });
        const distinctOffices = Array.from(distinctOfficesMap.values());

        const offices =
            officeListData?.content
                .map((office) => ({
                    ...office,
                    matched: distinctOffices.some((projectOffice) => projectOffice.id === office.id),
                }))
                .sort((a, b) => {
                    if (a.matched !== b.matched) {
                        return a.matched ? -1 : 1;
                    }
                    if (a.abbreviation < b.abbreviation) return -1;
                    if (a.abbreviation > b.abbreviation) return 1;
                    return 0;
                }) ?? [];

        return {
            supervisedByEmployees: supervisedByEmployees,
            responsibleEmployees: responsibleEmployees,
            handledByEmployees: handledByEmployees,
            connectedPracticeAreas: practiceAreas,
            connectedOffices: offices.sort(),
        };
    }, [experienceRecord.matters, officeListData?.content]);

    if (!experienceRecord) {
        return null;
    }

    // A new-design label with its field's info icon after it, so it wraps in the card's narrow cells where the
    // field's label does.
    const hintedLabel = (field: string, classicId: string) =>
        isNew
            ? infoLabel(`experience-records.form.${field}`, `experience-records.form.${field}-tooltip`)
            : t(classicId);
    // The new design's rows carry their field's label and come in the form's order; classic keeps its own.
    // The status edits as a row of radio options, so its read row spans the card as well.
    const status: Row = {
        label: isNew
            ? infoLabel('experience-records.form.status', 'experience-records.form.status-tooltip')
            : t('experience-records.view.status'),
        content: t(`experience-records.status.${experienceRecord.status}`),
        wide: isNew ? 'always' : undefined,
    };
    const client: Row = {
        label: t(isNew ? 'experience-records.form.client' : 'experience-records.view.client'),
        content: experienceRecord.clientParty?.name || '-',
    };
    const clientName: Row = {
        label: t(
            isNew
                ? 'experience-records.form.client-name-description'
                : 'experience-records.view.client-name-description'
        ),
        content: experienceRecord.clientName || '-',
    };
    // The client's areas and sectors: text areas in the form, which shows them without editing them.
    const businessAreas: Row = {
        label: hintedLabel('connected-business-areas', 'experience-records.view.connected-business-areas'),
        content: connectedBusinessAreas || '-',
        multiline: isNew,
    };
    const sectors: Row = {
        label: hintedLabel('connected-sectors', 'experience-records.view.connected-sectors'),
        content: connectedSectors || '-',
        multiline: isNew,
    };
    const jurisdictions: Row = isNew
        ? {
              label: t('experience-records.form.jurisdictions'),
              content: '-',
              chips: (experienceRecord.countryTypeCodes ?? []).map((typeCode) => ({
                  key: typeCode,
                  label: getClassifierText(classifiers['COUNTRY'], typeCode),
              })),
          }
        : {
              label: t('experience-records.view.jurisdictions'),
              content: experienceRecord.countryTypeCodes?.length
                  ? experienceRecord.countryTypeCodes
                        .map((typeCode) => getClassifierText(classifiers['COUNTRY'], typeCode))
                        .sort()
                        .join(', ')
                  : '-',
          };
    const confidentiality: Row = isNew
        ? {
              label: infoLabel(
                  'experience-records.form.is-confidential',
                  'experience-records.form.is-confidential-tooltip'
              ),
              content: experienceRecord.isConfidential ? t('yes') : t('no'),
          }
        : {
              label: t('experience-records.view.confidentiality'),
              content: experienceRecord.isConfidential ? t('experience-records.view.xr-is-confidential') : t('no'),
          };
    const date: Row = {
        label: t(isNew ? 'experience-records.form.date' : 'experience-records.view.experience-record-date'),
        content: <FormattedDate value={experienceRecord.date} />,
    };
    const consentRequestedOn: Row = {
        label: hintedLabel('client-consent-requested-on', 'experience-records.view.client-consent-requested-on'),
        content: <FormattedDate value={experienceRecord.clientConsentRequestedOn} />,
    };
    const consentReceivedOn: Row = {
        label: hintedLabel('client-consent-received-on', 'experience-records.view.client-consent-received-on'),
        content: <FormattedDate value={experienceRecord.clientConsentReceivedOn} />,
    };
    const nameRow: Row = {
        label: hintedLabel('xr-name', 'experience-records.view.xr-name'),
        content: experienceRecord.name || '-',
    };
    const getCoreDetailsData = (): Row[] =>
        isNew
            ? [
                  status,
                  client,
                  clientName,
                  confidentiality,
                  businessAreas,
                  sectors,
                  jurisdictions,
                  date,
                  consentRequestedOn,
                  consentReceivedOn,
                  nameRow,
              ]
            : [
                  status,
                  client,
                  clientName,
                  businessAreas,
                  sectors,
                  jurisdictions,
                  confidentiality,
                  date,
                  consentRequestedOn,
                  consentReceivedOn,
              ];

    const getOurTeamData = (): Row[] => [
        {
            label: t('experience-records.view.supervised-by'),
            content: derivedTeamData.supervisedByEmployees.length
                ? getLinks('employee', derivedTeamData.supervisedByEmployees)
                : '-',
        },
        {
            label: t('experience-records.view.responsible-lawyers'),
            content: derivedTeamData.responsibleEmployees.length
                ? getLinks('employee', derivedTeamData.responsibleEmployees)
                : '-',
        },
        {
            label: t('experience-records.view.handled-by-employees'),
            content: derivedTeamData.handledByEmployees.length
                ? getLinks('employee', derivedTeamData.handledByEmployees)
                : '-',
        },
    ];

    const getConnectedCasesData = (): Row[] => [
        isNew
            ? {
                  label: t('experience-records.form.connected-cases'),
                  content: '-',
                  // One chip a case, as the field has them, with its indicators, each still the case's link.
                  chips: getCaseChips(experienceRecord.matters ?? []),
              }
            : {
                  label: t('experience-records.view.connected-cases'),
                  content: experienceRecord.matters?.length
                      ? getLinks(
                            'matter',
                            experienceRecord.matters.map((matter) => ({
                                id: matter.id,
                                name: matter.fullName,
                            }))
                        )
                      : '-',
              },
        {
            label: t('experience-records.view.connected-practice-areas'),
            readOnly: true,
            content: derivedTeamData.connectedPracticeAreas?.length ? (
                <span className="flex flex-col">
                    {derivedTeamData.connectedPracticeAreas
                        .toSorted((a, b) => a.fullPath.localeCompare(b.fullPath))
                        .map((item) => (
                            <span key={item.id} className="text-body1">
                                {item.fullPath}
                            </span>
                        ))}
                </span>
            ) : (
                '-'
            ),
        },
        {
            label: t('experience-records.view.connected-offices'),
            wide: true,
            readOnly: true,
            content: (
                <span className="flex flex-row flex-wrap gap-1">
                    {derivedTeamData.connectedOffices?.map((office) => (
                        <StatusChip
                            key={office.id}
                            label={office.abbreviation}
                            type={office.matched ? 'neutral' : 'inactive'}
                        />
                    )) || '-'}
                </span>
            ),
        },
    ];

    const getMarketingData = (): Row[] => [
        {
            label: hintedLabel('is-showcase-priority', 'experience-records.view.showcase-priority'),
            content: experienceRecord.isShowcasePriority ? t('yes') : t('no'),
        },
        {
            label: hintedLabel('is-used-in-marketing', 'experience-records.view.used-in-marketing'),
            content: experienceRecord.isUsedInMarketing ? t('yes') : t('no'),
        },
        {
            label: hintedLabel(
                'is-included-in-legal-directories',
                'experience-records.view.included-in-legal-directories'
            ),
            content: experienceRecord.isIncludedInLegalDirectories ? t('yes') : t('no'),
        },
        isNew
            ? {
                  label: hasRankingsReferees
                      ? infoLabel(
                            'experience-records.form.referee-candidates',
                            'experience-records.form.referee-candidates-tooltip'
                        )
                      : infoLabel(
                            'experience-records.form.referee-name',
                            'experience-records.form.referee-name-tooltip'
                        ),
                  content: '-',
                  // By name, as the form orders its values.
                  chips: sortByField(experienceRecord.parties ?? [], 'name').map((party) => ({
                      key: party.id,
                      label: getLinks('contact', [party]),
                  })),
              }
            : {
                  label: t(
                      hasRankingsReferees
                          ? 'experience-records.view.referee-candidates'
                          : 'experience-records.view.referees'
                  ),
                  content: experienceRecord.parties?.length ? getLinks('contact', experienceRecord.parties) : '-',
              },
        ...(hasRankingsReferees
            ? [
                  {
                      label: isNew
                          ? infoLabel(
                                'experience-records.view.candidate-nominations',
                                'experience-records.view.candidate-nominations-tooltip'
                            )
                          : t('experience-records.view.candidate-nominations'),
                      wide: true,
                      readOnly: true,
                      content:
                          nominatedCandidates.length === 0 ? (
                              '-'
                          ) : (
                              <div className="flex flex-col gap-3">
                                  {nominatedCandidates.map((person) => (
                                      <div key={person.id} className="flex flex-col gap-1">
                                          <span className="text-body2 font-semibold">{person.name}</span>
                                          <ul className="m-0 flex list-none flex-col gap-1 p-0">
                                              {nominationsOf(person.id).map((nomination) => (
                                                  <li
                                                      key={nomination.id}
                                                      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1"
                                                  >
                                                      <span className="text-body2 min-w-0 flex-[1_1_12rem]">
                                                          {nominationName(
                                                              getClassifierText(
                                                                  classifiers['LEGAL_DIRECTORY'],
                                                                  nomination.legalDirectoryCode
                                                              ),
                                                              nomination.editionYear,
                                                              nomination.practiceArea?.name
                                                          )}
                                                      </span>
                                                      <span className="text-body2 text-fg-secondary new:text-muted-foreground">
                                                          {t(`rankings-referees.status.${nomination.statusCode}`)}
                                                      </span>
                                                  </li>
                                              ))}
                                          </ul>
                                      </div>
                                  ))}
                                  <RouterLink
                                      className="entity-link text-link w-fit text-sm no-underline hover:underline"
                                      // The list's Referee filter on the candidates, so it shows the nominations listed here.
                                      to={`/rankings-referees?mf=${encodeURIComponent(candidateFilter)}`}
                                  >
                                      {t('experience-records.view.open-in-rankings-referees')}
                                  </RouterLink>
                              </div>
                          ),
                  },
              ]
            : []),
    ];

    const descriptionRows: Row[] = [
        {
            label: hintedLabel('description', 'experience-records.view.description-english'),
            content: experienceRecord.description || '-',
            multiline: true,
        },
        {
            label: t(
                isNew
                    ? 'experience-records.form.description-local-lang'
                    : 'experience-records.view.description-local-lang'
            ),
            content: experienceRecord.descriptionLocalLang || '-',
            multiline: true,
        },
    ];
    const numericValue = parseFloat(experienceRecord.valueAmount as string);
    const isNumericValue = !isNaN(numericValue) && isFinite(numericValue);
    const valueRow: Row = {
        label: hintedLabel('value-amount', 'experience-records.view.deal-dispute-value'),
        content: experienceRecord.valueAmount ? (
            <span className="flex flex-row items-center gap-4">
                {experienceRecord.isValueConfidential && (
                    <XrConfidentialMarker label={t('experience-records.form.is-value-confidential')} />
                )}
                {/* The new design has the currency in a row of its own, as the form has it in a field of its own. */}
                {isNumericValue && (isNew || experienceRecord.currencyTypeCode) ? (
                    <MoneyDisplay
                        amount={experienceRecord.valueAmount}
                        currency={isNew ? undefined : experienceRecord.currencyTypeCode}
                    />
                ) : (
                    <span className="text-body1">{experienceRecord.valueAmount}</span>
                )}
            </span>
        ) : (
            '-'
        ),
    };

    if (isNew) {
        const connectedCasesRows = getConnectedCasesData();
        return (
            <div className={RECORD_GRID_CLASS}>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    <ExperienceRecordSection
                        id="core-details"
                        titleId="experience-records.view.titles.core-details"
                        experienceRecord={experienceRecord}
                    >
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="experience-records.view.titles.core-details"
                                data={getCoreDetailsData()}
                                button={edit}
                            />
                        )}
                    </ExperienceRecordSection>
                    <ExperienceRecordSection
                        id="description"
                        titleId="experience-records.view.titles.description"
                        experienceRecord={experienceRecord}
                    >
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="experience-records.view.titles.description"
                                data={descriptionRows}
                                button={edit}
                            />
                        )}
                    </ExperienceRecordSection>
                    <ExperienceRecordSection
                        id="value"
                        titleId="experience-records.view.titles.value"
                        experienceRecord={experienceRecord}
                    >
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="experience-records.view.titles.value"
                                data={[
                                    valueRow,
                                    {
                                        label: t('experience-records.form.currency'),
                                        content: experienceRecord.currencyTypeCode || '-',
                                    },
                                    {
                                        label: infoLabel(
                                            'experience-records.form.is-value-confidential',
                                            'experience-records.form.is-value-confidential-tooltip'
                                        ),
                                        content: experienceRecord.isValueConfidential ? t('yes') : t('no'),
                                    },
                                ]}
                                button={edit}
                            />
                        )}
                    </ExperienceRecordSection>
                </div>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    <GridItemList
                        isNew
                        titleId="experience-records.view.titles.our-team-role"
                        data={getOurTeamData()}
                    />
                    <ExperienceRecordSection
                        id="connected-cases"
                        titleId="experience-records.view.titles.connected-cases"
                        experienceRecord={experienceRecord}
                        rows={connectedCasesRows}
                    >
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="experience-records.view.titles.connected-cases"
                                data={connectedCasesRows}
                                button={edit}
                            />
                        )}
                    </ExperienceRecordSection>
                    <ExperienceRecordSection
                        id="marketing"
                        titleId="experience-records.view.titles.business-development-marketing"
                        experienceRecord={experienceRecord}
                    >
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="experience-records.view.titles.business-development-marketing"
                                data={getMarketingData()}
                                button={edit}
                            />
                        )}
                    </ExperienceRecordSection>
                </div>
            </div>
        );
    }

    return (
        // `items-start`: each column is as tall as its own cards, not stretched to the taller one.
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
            <div className="flex flex-col gap-6">
                <GridItemList
                    titleId="experience-records.view.titles.our-team-role"
                    size={12}
                    data={getOurTeamData()}
                    key="xr-team"
                />
                <GridItemList
                    titleId="experience-records.view.titles.business-development-marketing"
                    size={12}
                    data={getMarketingData()}
                    key="xr-marketing"
                />
                <GridItemList
                    titleId="experience-records.view.titles.core-details"
                    size={12}
                    data={getCoreDetailsData()}
                    key="xr-core-details"
                />
                <GridItemList
                    titleId="experience-records.view.titles.connected-cases"
                    size={12}
                    data={getConnectedCasesData()}
                    key="xr-connected-cases"
                />
            </div>
            <GridItemList
                titleId="experience-records.view.titles.description"
                size={12}
                data={[nameRow, ...descriptionRows, valueRow]}
                key="xr-description"
            />
        </div>
    );
};

export default GeneralView;
